import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import MoreHorizOutlined from '@mui/icons-material/MoreHorizOutlined';
import StarOutlined from '@mui/icons-material/StarOutlined';
import StarOutlineOutlined from '@mui/icons-material/StarOutlineOutlined';
import SubdirectoryArrowLeftOutlined from '@mui/icons-material/SubdirectoryArrowLeftOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { ELLIPSIS_SX } from '../../creatives/utils.js';
import { STATUS_PILL } from '../../dashboard/activityRows.js';
import { runLabelOf } from '../automations/RunLabel.js';
import { Pill, STATUS_PILL_SX } from '../Pill.js';
import { failureReason } from '../runFailure.js';
import type { WorkflowRunRow } from './runRows.js';

const COLUMNS =
  '32px minmax(0, 1.8fr) minmax(0, 1.2fr) minmax(0, 1.3fr) minmax(0, 1.4fr) minmax(0, 1fr) 150px';

const HEADINGS = ['', 'Workflow', 'Date and time', 'Chained', 'Outcomes', 'Owner'];

// How many chain or outcome chips a cell shows before the rest go behind "+N".
const CHIPS_SHOWN = 2;

const FRAME_SX = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  overflow: 'hidden',
  bgcolor: 'background.paper',
};

const HEAD_SX = {
  display: 'grid',
  gridTemplateColumns: COLUMNS,
  gap: 2,
  py: 2,
  px: 3,
  bgcolor: 'background.page',
  borderBottom: '1px solid',
  borderColor: 'divider',
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  color: 'text.disabled',
};

const ROW_SX = {
  display: 'grid',
  gridTemplateColumns: COLUMNS,
  gap: 2,
  px: 3,
  minHeight: 68,
  alignItems: 'center',
  borderBottom: '1px solid',
  borderColor: 'divider',
  transition: 'background-color .15s ease',
  '&:last-of-type': { borderBottom: 0 },
  '&:hover': { bgcolor: 'action.hover' },
};

const CHIP_SX = {
  px: 1,
  py: 0.25,
  borderRadius: 0.75,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 0,
  textTransform: 'uppercase',
  bgcolor: 'background.platform',
  color: 'text.secondary',
  cursor: 'pointer',
  border: 0,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  whiteSpace: 'nowrap',
  '&:hover': { color: 'primary.main' },
};

const MORE_CHIP_SX = {
  ...CHIP_SX,
  bgcolor: 'text.primary',
  color: 'common.white',
  '&:hover': { color: 'common.white' },
};

export interface RunsTableProps {
  rows: WorkflowRunRow[];
  starred: string[];
  rerunning: string | null;
  onToggleStar: (runId: string) => void;
  onOpenRun: (runId: string) => void;
  onOpenOutcome: (row: WorkflowRunRow, runId: string) => void;
  onRerun: (row: WorkflowRunRow) => void;
  onDownloadOutcomes: (row: WorkflowRunRow) => void;
}

function detailOf(row: WorkflowRunRow): string | null {
  const parts = [
    row.automation === null ? null : runLabelOf(row.automation),
    row.status === 'failed' ? failureReason(row.error) : null,
  ].filter((part) => part !== null);
  return parts.length === 0 ? null : parts.join(' · ');
}

function when(row: WorkflowRunRow): string {
  const date = new Date(row.completedAt ?? row.startedAt);
  return `${date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })} · ${date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
}

function Chips({
  labels,
  onOpen,
}: {
  labels: Array<{ key: string; text: string; lead: ReactNode }>;
  onOpen: (key: string) => void;
}): ReactNode {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const shown = labels.slice(0, CHIPS_SHOWN);
  const rest = labels.slice(CHIPS_SHOWN);

  return (
    <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center', minWidth: 0 }}>
      {shown.map((label) => (
        <Box
          key={label.key}
          component="button"
          type="button"
          onClick={() => onOpen(label.key)}
          sx={CHIP_SX}
        >
          {label.lead}
          <Box component="span" sx={ELLIPSIS_SX}>
            {label.text}
          </Box>
        </Box>
      ))}
      {rest.length === 0 ? null : (
        <>
          <Box
            component="button"
            type="button"
            aria-label={`Show ${rest.length} more`}
            onMouseEnter={(event: React.MouseEvent<HTMLElement>) => setAnchor(event.currentTarget)}
            onClick={(event: React.MouseEvent<HTMLElement>) => setAnchor(event.currentTarget)}
            sx={MORE_CHIP_SX}
          >
            {`+${rest.length}`}
          </Box>
          <Menu
            open={anchor !== null}
            anchorEl={anchor}
            onClose={() => setAnchor(null)}
            slotProps={{ list: { onMouseLeave: () => setAnchor(null) } }}
          >
            {rest.map((label) => (
              <MenuItem
                key={label.key}
                onClick={() => {
                  setAnchor(null);
                  onOpen(label.key);
                }}
                sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}
              >
                {label.text}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Stack>
  );
}

function RowMenu({
  canDownload,
  onDownload,
}: {
  canDownload: boolean;
  onDownload: () => void;
}): ReactNode {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <IconButton
        size="small"
        aria-label="More"
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        <MoreHorizOutlined sx={{ fontSize: 18 }} />
      </IconButton>
      <Menu open={anchor !== null} anchorEl={anchor} onClose={() => setAnchor(null)}>
        <MenuItem
          disabled={!canDownload}
          onClick={() => {
            setAnchor(null);
            onDownload();
          }}
        >
          Download outcomes
        </MenuItem>
      </Menu>
    </>
  );
}

export function RunsTable({
  rows,
  starred,
  rerunning,
  onToggleStar,
  onOpenRun,
  onOpenOutcome,
  onRerun,
  onDownloadOutcomes,
}: RunsTableProps): ReactNode {
  return (
    <Box data-testid="table-runs" sx={FRAME_SX}>
      <Box sx={HEAD_SX}>
        {HEADINGS.map((heading, index) => (
          <Box key={heading === '' ? `blank-${index}` : heading} component="span">
            {heading}
          </Box>
        ))}
        <Box component="span" sx={{ textAlign: 'center' }}>
          Action
        </Box>
      </Box>

      {rows.map((row) => {
        const chained = [
          ...(row.chainedFrom === null
            ? []
            : [
                {
                  key: row.chainedFrom.runId,
                  text: row.chainedFrom.workflow,
                  lead: <SubdirectoryArrowLeftOutlined sx={{ fontSize: 12 }} />,
                },
              ]),
          ...row.chainedInto.map((into) => ({
            key: into.runId,
            text: into.workflow,
            lead: <ArrowForwardOutlined sx={{ fontSize: 12 }} />,
          })),
        ];

        const detail = detailOf(row);

        return (
          <Box key={row.runId} data-testid={`run-${row.runId}`} sx={ROW_SX}>
            <IconButton
              size="small"
              aria-label={starred.includes(row.runId) ? 'Unstar this run' : 'Star this run'}
              onClick={() => onToggleStar(row.runId)}
            >
              {starred.includes(row.runId) ? (
                <StarOutlined sx={{ fontSize: 16, color: 'warning.main' }} />
              ) : (
                <StarOutlineOutlined sx={{ fontSize: 16, color: 'text.disabled' }} />
              )}
            </IconButton>

            <Tooltip placement="top-start" title={row.title}>
              <Stack sx={{ minWidth: 0 }}>
                <Stack direction="row" sx={{ gap: 1, alignItems: 'center', minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, ...ELLIPSIS_SX }}>{row.workflow}</Typography>
                  {row.status === 'completed' ? null : (
                    <Pill
                      {...(STATUS_PILL_SX[row.status] ? { sx: STATUS_PILL_SX[row.status] } : {})}
                    >
                      {STATUS_PILL[row.status]}
                    </Pill>
                  )}
                </Stack>
                {detail === null ? null : (
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', ...ELLIPSIS_SX }}>
                    {detail}
                  </Typography>
                )}
              </Stack>
            </Tooltip>

            <Typography sx={{ color: 'text.secondary', ...ELLIPSIS_SX }}>{when(row)}</Typography>

            <Chips labels={chained} onOpen={onOpenRun} />

            <Chips
              labels={row.outcomes.map((outcome) => ({
                key: outcome.runId,
                text: outcome.kind,
                lead: null,
              }))}
              onOpen={(runId) => onOpenOutcome(row, runId)}
            />

            <Typography sx={ELLIPSIS_SX}>{row.owner ?? '—'}</Typography>

            <Stack
              direction="row"
              sx={{ gap: 0.5, alignItems: 'center', justifyContent: 'center' }}
            >
              {/* A chained run needs its parent's selection, which is not stored. */}
              <Button
                size="small"
                variant="outlined"
                disabled={
                  row.parentRunId !== null || row.status === 'running' || rerunning !== null
                }
                onClick={() => onRerun(row)}
              >
                Re-run
              </Button>
              <Button size="small" onClick={() => onOpenRun(row.runId)}>
                View
              </Button>
              <RowMenu
                canDownload={row.status === 'completed'}
                onDownload={() => onDownloadOutcomes(row)}
              />
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
}
